import { getCurrentLayer } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useGetCategoriesQuery } from "@builder/store/slices/dataApiSlices/categoriesApiSlice";
import { useGetOrdersQuery } from "@builder/store/slices/dataApiSlices/ordersApiSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { Category } from "@builder/types/Categories.type";
import { QueryFieldsProducts } from "@common/build/constants";
import { FilterNode, OrderQueries, OrderQueriesKey } from "@common/build/types";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface UseFilterOptionsProps {
    node: FilterNode;
}

const useFilterOptions = ({ node }: UseFilterOptionsProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const companyId = useAppSelector(state => state.storeData.data?.company?._id);
    const targetID = node.filter?.target;
    const baseLayer = useAppSelector(getCurrentLayer);
    const target = targetID ? baseLayer.components[targetID] : null;
    const targetData = target?.query?.dataType;

    const { data: ordersData } = useGetOrdersQuery(companyId ? { companyId, limit: 10, includeEmployee: true } : skipToken);
    const { data: categoriesData } = useGetCategoriesQuery(companyId ? { companyId, limit: 10 } : skipToken);

    const productOptions: Record<string, string> = {
        [QueryFieldsProducts.ONSALE]: t("generalComponents.selectInput.On Sale"),
        [QueryFieldsProducts.CATEGORIES]: t("generalComponents.selectInput.Categories")
    };

    type SelectedOrderQueries = Pick<OrderQueries, OrderQueriesKey.ORDER_SERVED_BY | OrderQueriesKey.ORDER_STATUS | OrderQueriesKey.ORDER_SOURCE>;
    const orderOptions: Record<keyof SelectedOrderQueries, string> = {
        orderServedBy: t("generalComponents.selectInput.Order Employee"),
        orderStatus: t("generalComponents.selectInput.Order Status"),
        orderSource: t("generalComponents.selectInput.Order Source")
    };

    const customersOptions: Record<string, string> = {
        customerSource: t("generalComponents.selectInput.Customer Source")
    };

    const options = targetData === "orders" ? orderOptions : targetData === "customers" ? customersOptions : productOptions;
    const value = node.filter?.typeOfFilter || QueryFieldsProducts.ONSALE;
    const callback = (newValue: string) => {
        dispatch(updateNodeProperty({ propertyPath: ["filter", "typeOfFilter"], value: newValue }));
    };

    useEffect(() => {
        const updatePreviewOptions = async () => {
            if (targetData === "products") {
                if (node.filter?.typeOfFilter === QueryFieldsProducts.CATEGORIES) {
                    if (companyId) {
                        const categories = categoriesData || [];
                        if (categories && categories.length > 0) {
                            const data = categories.reduce((acc: Record<string, string>, category: Category) => {
                                acc[category._id] = category.name || "";
                                return acc;
                            }, {});
                            dispatch(updateNodeProperty({ propertyPath: ["previewOptions"], remove: true, value: {} }));
                            dispatch(
                                updateNodeProperty({
                                    propertyPath: ["previewOptions"],
                                    value: { all: t("generalComponents.selectInput.All categories"), ...data }
                                })
                            );
                        }
                    }
                } else {
                    dispatch(updateNodeProperty({ propertyPath: ["previewOptions"], remove: true, value: {} }));
                    dispatch(
                        updateNodeProperty({
                            propertyPath: ["previewOptions"],
                            value: {
                                all: t("generalComponents.selectInput.All products"),
                                isOnSale: t("generalComponents.selectInput.On sale")
                            }
                        })
                    );
                }
            } else if (targetData === "orders") {
                if (companyId) {
                    const orders = ordersData || [];
                    const employees = orders
                        .map(order => order.employee)
                        .filter(employee => employee && typeof employee !== "string" && employee._id);
                    const uniqueEmployees = employees.reduce((acc: Record<string, string>, employee) => {
                        if (!employee || typeof employee === "string") return acc;
                        const fullName = `${employee?.firstName ? employee?.firstName + " " : ""}${employee.lastName}`;
                        acc[employee._id!] = fullName;
                        return acc;
                    }, {});
                    if (Object.keys(uniqueEmployees).length === 0) {
                        //mockup employees:
                        uniqueEmployees["1"] = t("generalComponents.selectInput.Employee") + " 1";
                        uniqueEmployees["2"] = t("generalComponents.selectInput.Employee") + " 2";
                        uniqueEmployees["3"] = t("generalComponents.selectInput.Employee") + " 3";
                    }
                    if (node.filter?.typeOfFilter === "orderStatus") {
                        dispatch(updateNodeProperty({ propertyPath: ["previewOptions"], remove: true, value: {} }));
                        dispatch(
                            updateNodeProperty({
                                propertyPath: ["previewOptions"],
                                value: {
                                    all: t("generalComponents.selectInput.All statuses"),
                                    pending: t("generalComponents.selectInput.Pending"),
                                    completed: t("generalComponents.selectInput.Completed"),
                                    refunded: t("generalComponents.selectInput.Refunded"),
                                    cancelled: t("generalComponents.selectInput.Cancelled"),
                                    parked: t("generalComponents.selectInput.Parked")
                                }
                            })
                        );
                    } else if (node.filter?.typeOfFilter === "orderSource") {
                        dispatch(updateNodeProperty({ propertyPath: ["previewOptions"], remove: true, value: {} }));
                        dispatch(
                            updateNodeProperty({
                                propertyPath: ["previewOptions"],
                                value: {
                                    all: t("generalComponents.selectInput.All sources"),
                                    pos: t("generalComponents.selectInput.POS"),
                                    thirdParty: t("generalComponents.selectInput.Third party")
                                }
                            })
                        );
                    } else if (node.filter?.typeOfFilter === "orderServedBy") {
                        dispatch(updateNodeProperty({ propertyPath: ["previewOptions"], remove: true, value: {} }));
                        dispatch(
                            updateNodeProperty({
                                propertyPath: ["previewOptions"],
                                value: { all: t("generalComponents.selectInput.All employees"), ...uniqueEmployees }
                            })
                        );
                    }
                }
            } else if (targetData === "customers") {
                dispatch(updateNodeProperty({ propertyPath: ["previewOptions"], remove: true, value: {} }));
                dispatch(
                    updateNodeProperty({
                        propertyPath: ["previewOptions"],
                        value: {
                            all: t("generalComponents.selectInput.All customers"),
                            inStore: t("generalComponents.selectInput.In store"),
                            thirdParty: t("generalComponents.selectInput.Third party")
                        }
                    })
                );
            }
        };
        void updatePreviewOptions();
    }, [companyId, node.filter?.typeOfFilter, dispatch, t, targetData]);

    return { options, value, callback, target };
};

export default useFilterOptions;
